/**
 * Mock data for Client Portal Phase 4 surfaces.
 *
 * The Client Portal UI ships ahead of its backend API in Phase 4. Each
 * page renders this frozen example data behind a MockDataBanner so the
 * shape of the surface (layout, interactions, IA) can be validated by
 * stakeholders without waiting on the /api/portal/* endpoints.
 *
 * Replacement strategy: each export below will be replaced by a Pinia
 * store action that calls the corresponding /api/portal/* endpoint
 * (see PLAN.md section "Phase 4 deliverables"). The shape of each mock
 * object mirrors the eventual JSON:API resource representation so the
 * components can swap their data source with minimal template churn.
 *
 * All exports are Object.freeze()'d to prevent accidental mutation in
 * components -- mutation would otherwise leak across page navigations.
 */

// Client identity + branding. Replaced by GET /api/portal/profile.
// Branding color is consumed by the Login page for accent styling.
export const mockClientProfile = Object.freeze({
  id: 42,
  client_name: "Acme Industries",
  distributor_name: "ThreadFlow Demo Distributor",
  plan_tier: "Pro",
  brand_color: "#1976d2",
  contact_email: "merch@acme.example.com",
});

// Overview KPI rollups. Replaced by GET /api/portal/overview.
// Cents are stored as integers throughout to mirror the backend.
export const mockOverviewMetrics = Object.freeze({
  orders_in_flight: 7,
  awaiting_approval: 3,
  spent_30d_cents: 1248750, // $12,487.50
  remaining_budget_cents: 3751250, // $37,512.50
});

// Orders. Replaced by GET /api/portal/orders.
// Status vocabulary: 'pending' | 'in_production' | 'shipped' | 'delivered'
// | 'cancelled'. The backend will likely return a richer set; the mock
// data covers the common cases the UI must render today.
export const mockOrders = Object.freeze([
  Object.freeze({
    id: 1001,
    order_number: "ORD-1001",
    placed_by_email: "alice@acme.example.com",
    total_cents: 248750,
    status: "shipped",
    placed_at: "2026-05-28T14:22:00Z",
    line_count: 4,
  }),
  Object.freeze({
    id: 1002,
    order_number: "ORD-1002",
    placed_by_email: "bob@acme.example.com",
    total_cents: 89900,
    status: "in_production",
    placed_at: "2026-05-29T09:15:00Z",
    line_count: 2,
  }),
  Object.freeze({
    id: 1003,
    order_number: "ORD-1003",
    placed_by_email: "alice@acme.example.com",
    total_cents: 412300,
    status: "delivered",
    placed_at: "2026-05-22T18:01:00Z",
    line_count: 6,
  }),
  Object.freeze({
    id: 1004,
    order_number: "ORD-1004",
    placed_by_email: "carol@acme.example.com",
    total_cents: 175000,
    status: "pending",
    placed_at: "2026-06-01T11:45:00Z",
    line_count: 3,
  }),
  Object.freeze({
    id: 1005,
    order_number: "ORD-1005",
    placed_by_email: "dave@acme.example.com",
    total_cents: 56200,
    status: "shipped",
    placed_at: "2026-05-30T08:30:00Z",
    line_count: 1,
  }),
  Object.freeze({
    id: 1006,
    order_number: "ORD-1006",
    placed_by_email: "alice@acme.example.com",
    total_cents: 320000,
    status: "in_production",
    placed_at: "2026-06-02T13:20:00Z",
    line_count: 5,
  }),
  Object.freeze({
    id: 1007,
    order_number: "ORD-1007",
    placed_by_email: "eve@acme.example.com",
    total_cents: 78400,
    status: "cancelled",
    placed_at: "2026-05-25T16:55:00Z",
    line_count: 2,
  }),
  Object.freeze({
    id: 1008,
    order_number: "ORD-1008",
    placed_by_email: "bob@acme.example.com",
    total_cents: 195600,
    status: "delivered",
    placed_at: "2026-05-20T10:10:00Z",
    line_count: 3,
  }),
  Object.freeze({
    id: 1009,
    order_number: "ORD-1009",
    placed_by_email: "carol@acme.example.com",
    total_cents: 142500,
    status: "shipped",
    placed_at: "2026-05-31T15:00:00Z",
    line_count: 2,
  }),
  Object.freeze({
    id: 1010,
    order_number: "ORD-1010",
    placed_by_email: "alice@acme.example.com",
    total_cents: 264800,
    status: "pending",
    placed_at: "2026-06-03T09:30:00Z",
    line_count: 4,
  }),
]);

// Pending approvals queue. Replaced by GET /api/portal/approvals.
// Type vocabulary: 'decoration' | 'order' | 'budget'. The shape matches
// the umbrella decoration-approval PLAN's payload contract so the UI
// can re-use the same row template once that endpoint lights up.
export const mockApprovals = Object.freeze([
  Object.freeze({
    id: 2001,
    type: "decoration",
    summary: "Front-chest embroidery on Port Authority K500 polos",
    awaiting_since: "2026-06-01T10:00:00Z",
    requester: "Distributor: ThreadFlow Demo Distributor",
  }),
  Object.freeze({
    id: 2002,
    type: "order",
    summary: "Bulk order ORD-1004 ($1,750.00) -- exceeds shopper limit",
    awaiting_since: "2026-06-02T08:30:00Z",
    requester: "carol@acme.example.com",
  }),
  Object.freeze({
    id: 2003,
    type: "budget",
    summary: "Marketing category budget increase request: +$2,500.00",
    awaiting_since: "2026-05-30T14:15:00Z",
    requester: "alice@acme.example.com",
  }),
  Object.freeze({
    id: 2004,
    type: "decoration",
    summary: "Left-sleeve screen print on Gildan 5000 tees (3-color)",
    awaiting_since: "2026-06-03T09:00:00Z",
    requester: "Distributor: ThreadFlow Demo Distributor",
  }),
  Object.freeze({
    id: 2005,
    type: "order",
    summary: "Rush order ORD-1010 -- requires expedited shipping approval",
    awaiting_since: "2026-06-03T11:45:00Z",
    requester: "alice@acme.example.com",
  }),
]);

// Budget line items. Replaced by GET /api/portal/budget.
// Period values: 'monthly' | 'quarterly' | 'annual'.
export const mockBudgetLines = Object.freeze([
  Object.freeze({
    id: 3001,
    name: "Marketing",
    limit_cents: 1500000,
    spent_cents: 875000,
    period: "monthly",
  }),
  Object.freeze({
    id: 3002,
    name: "Sales Team",
    limit_cents: 800000,
    spent_cents: 418000,
    period: "monthly",
  }),
  Object.freeze({
    id: 3003,
    name: "Customer Gifts",
    limit_cents: 500000,
    spent_cents: 488000,
    period: "monthly",
  }),
  Object.freeze({
    id: 3004,
    name: "Trade Shows",
    limit_cents: 2500000,
    spent_cents: 1240000,
    period: "quarterly",
  }),
  Object.freeze({
    id: 3005,
    name: "Employee Onboarding",
    limit_cents: 1800000,
    spent_cents: 320000,
    period: "annual",
  }),
  Object.freeze({
    id: 3006,
    name: "Executive Travel",
    limit_cents: 600000,
    spent_cents: 590000,
    period: "monthly",
  }),
]);

// Team / accounts. Replaced by GET /api/portal/team.
// Role vocabulary mirrors PLAN.md: 'client_admin' | 'store_manager'
// (the spec uses 'client_shopper' as an alias for the latter -- we
// keep 'client_admin' | 'client_shopper' here to match the prompt).
export const mockTeamMembers = Object.freeze([
  Object.freeze({
    id: 4001,
    name: "Alice Anderson",
    email: "alice@acme.example.com",
    role: "client_admin",
    invited_at: "2026-04-01T12:00:00Z",
    last_login_at: "2026-06-03T08:15:00Z",
  }),
  Object.freeze({
    id: 4002,
    name: "Bob Brown",
    email: "bob@acme.example.com",
    role: "client_shopper",
    invited_at: "2026-04-15T09:30:00Z",
    last_login_at: "2026-06-02T14:22:00Z",
  }),
  Object.freeze({
    id: 4003,
    name: "Carol Chen",
    email: "carol@acme.example.com",
    role: "client_shopper",
    invited_at: "2026-04-20T10:45:00Z",
    last_login_at: "2026-06-01T11:00:00Z",
  }),
  Object.freeze({
    id: 4004,
    name: "Dave Davis",
    email: "dave@acme.example.com",
    role: "client_shopper",
    invited_at: "2026-05-01T15:20:00Z",
    last_login_at: "2026-05-30T16:40:00Z",
  }),
  Object.freeze({
    id: 4005,
    name: "Eve Edwards",
    email: "eve@acme.example.com",
    role: "client_admin",
    invited_at: "2026-05-10T08:00:00Z",
    last_login_at: null,
  }),
]);

// NOTE: ``mockSettings`` was removed in the shipping-address redesign
// (Phase 3, 2026-07-31) along with the portal Settings page. Its three
// cards resolved as: notification preferences -> store config; default
// payment method -> deferred post-MVP (clients pay via the distributor's
// Stripe Connect account); default shipping address -> replaced by the
// structured client company/department addresses. See
// docs/planning/2026-07-31-shipping-address-and-checkout-shipping-redesign.md.
